// Top-up modal — 3 Razorpay-backed packs, triggered either by
// the Credits pill or by a 402 `insufficient_credits` response
// from a metered endpoint.
//
// In dev mode (no Razorpay creds on the server) the backend grants
// credits immediately, so this modal auto-closes on success.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Coins, Loader2, AlertTriangle } from 'lucide-react';
import { useCreditsStore } from '../../state/creditsStore';

function PackCard({ pack, onBuy, busy }) {
  const perCredit = pack.credits > 0 ? pack.price_inr / pack.credits : 0;
  const label = pack.code === 'pack_500' ? 'Starter'
    : pack.code === 'pack_1500' ? 'Best value'
      : pack.code === 'pack_3500' ? 'Power'
        : null;
  const highlight = pack.code === 'pack_1500';
  return (
    <div className={`relative flex flex-col rounded-xl border p-4 transition ${highlight ? 'border-emerald-500/60 bg-emerald-500/5 shadow-sm shadow-emerald-500/10' : 'border-border bg-card'}`}>
      {label && (
        <span className="absolute -top-2 right-4 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-emerald-600 text-white">
          {label}
        </span>
      )}
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Coins className="w-4 h-4 text-emerald-600" />
        {pack.credits.toLocaleString()} credits
      </div>
      <div className="mt-2 text-2xl font-bold text-foreground">
        ₹{pack.price_inr.toLocaleString()}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        ~₹{perCredit.toFixed(2)} / credit
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5">
        <div>· {Math.floor(pack.credits / 15)} AI chats</div>
        <div>· {Math.floor(pack.credits / 300)} pitch reports</div>
      </div>
      <button
        onClick={() => onBuy(pack.code)}
        disabled={busy}
        className={`mt-3 w-full rounded-md px-3 py-2 text-sm font-semibold transition ${highlight ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-foreground text-background hover:opacity-90'} disabled:opacity-50`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Buy now'}
      </button>
    </div>
  );
}

export default function TopupModal() {
  const {
    topupOpen, topupReason, topupBusy, topupError,
    packs, closeTopup, purchasePack, loadPacks, balance,
  } = useCreditsStore();

  // eslint-disable-next-line
  useEffect(() => { if (topupOpen && !packs?.length) loadPacks(); }, [topupOpen]);

  // Lock body scroll while the modal is open so the page behind
  // doesn't scroll with the mouse wheel.
  useEffect(() => {
    if (!topupOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [topupOpen]);

  if (!topupOpen) return null;

  // Render into document.body via a portal. The modal's parent tree
  // (GlobalTopBar) uses `backdrop-filter`, which creates a new
  // containing block and breaks `position: fixed`. Portalling past
  // it puts the overlay back on the viewport where it belongs.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={closeTopup}
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl my-8 rounded-2xl border border-border bg-background shadow-2xl"
        >
          <button
            onClick={closeTopup}
            className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-muted z-10"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="p-5 sm:p-6">
          <div className="flex items-center gap-2 text-lg font-bold">
            <Coins className="w-5 h-5 text-emerald-600" />
            {topupReason ? 'Out of credits' : 'Top up your credits'}
          </div>

          {topupReason ? (
            <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <div>
                You need <span className="font-semibold text-foreground">{topupReason.cost} credits</span> for
                this {labelForAction(topupReason.action)}. Current balance:{' '}
                <span className="font-semibold text-foreground">{topupReason.balance}</span>.
                Top up below or upgrade your plan to get a larger monthly grant.
              </div>
            </div>
          ) : (
            <div className="mt-2 text-sm text-muted-foreground">
              Current balance: <span className="font-semibold text-foreground">{balance ?? '—'}</span>.
              Use credits on AI chat (15/msg), pitch reports (300 each) and more.
            </div>
          )}

          {topupError && (
            <div className="mt-3 text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {topupError}
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(packs || []).map((p) => (
              <PackCard key={p.code} pack={p} onBuy={purchasePack} busy={topupBusy} />
            ))}
          </div>

          <div className="mt-4 text-[11px] text-muted-foreground text-center">
            Payments secured by Razorpay. Credits never expire. GST invoice emailed automatically.
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function labelForAction(action) {
  switch (action) {
    case 'ai_chat_message': return 'chat message';
    case 'pitch_report': return 'pitch report';
    case 'memo_generation': return 'memo generation';
    default: return 'action';
  }
}
